import React, { useState, useEffect } from "react";
import { Table, ProgressBar, Alert } from "react-bootstrap";
import Api from "../../utils/Api";
import Notifier from "../../components/Notifier";

export default function DesktopBudgetStatus() {
    const [budgetData, setBudgetData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentMonth, setCurrentMonth] = useState("");
    const notifierRef = React.createRef();

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            const now = new Date();
            const year = now.getFullYear();
            const month = now.getMonth() + 1;
            
            setCurrentMonth(`${getMonthName(month)} ${year}`);

            const { response } = await Api.getBudgetStatus({ year, month });
            
            if (response && response.budgetStatus) {
                setBudgetData(response.budgetStatus);
            }
        } catch (error) {
            console.error("Error loading budget status:", error);
            notifierRef?.current?.showError("שגיאה בטעינת נתונים");
        } finally {
            setLoading(false);
        }
    };

    const getMonthName = (month) => {
        const months = [
            "ינואר", "פברואר", "מרץ", "אפריל", "מאי", "יוני",
            "יולי", "אוגוסט", "ספטמבר", "אוקטובר", "נובמבר", "דצמבר"
        ];
        return months[month - 1];
    };

    const getProgressVariant = (percentage) => {
        if (percentage >= 100) return "danger";
        if (percentage >= 80) return "warning";
        return "success";
    };

    const getStatusText = (spent, limit, remaining) => {
        if (spent > limit) {
            return `חריגה של ₪${Math.abs(remaining).toLocaleString()}`;
        }
        return `נותרו ₪${remaining.toLocaleString()}`;
    };

    const cardStyle = {
        background: "white",
        boxShadow: "0 4px 8px 0 rgba(0,0,0,0.2)",
        borderRadius: "10px",
        width: "30%",
        margin: "auto",
        marginTop: "10px",
        padding: "20px"
    };

    const totalBudget = budgetData.reduce((sum, item) => sum + item.budgetLimit, 0);
    const totalSpent = budgetData.reduce((sum, item) => sum + item.spent, 0);
    const totalRemaining = totalBudget - totalSpent;
    const totalPercentage = totalBudget > 0 ? (totalSpent / totalBudget) * 100 : 0;

    return (
        <div style={{ marginTop: "78px" }}>
            <h2 style={{ textAlign: "center", marginBottom: "20px" }}>
                מצב תקציב - {currentMonth}
            </h2>

            {loading ? (
                <div style={{ textAlign: "center", padding: "50px" }}>טוען...</div>
            ) : (
                <>
                    {budgetData.length === 0 ? (
                        <Alert variant="info" style={{ width: "80%", margin: "20px auto", textAlign: "center" }}>
                            לא הוגדרו תקציבים לחודש זה. 
                            <br />
                            ניתן להגדיר תקציבים בעמוד ההגדרות.
                        </Alert>
                    ) : (
                        <>
                            {/* Summary Card */}
                            <div style={{ ...cardStyle, marginBottom: "30px", backgroundColor: "#f8f9fa" }}>
                                <h4 style={{ textAlign: "center", marginBottom: "20px" }}>סיכום כללי</h4>
                                <div style={{ display: "flex", justifyContent: "space-around", marginBottom: "15px" }}>
                                    <div style={{ textAlign: "center" }}>
                                        <div style={{ fontSize: "14px", color: "#666" }}>תקציב כולל</div>
                                        <div style={{ fontSize: "24px", fontWeight: "bold" }}>
                                            ₪{totalBudget.toLocaleString()}
                                        </div>
                                    </div>
                                    <div style={{ textAlign: "center" }}>
                                        <div style={{ fontSize: "14px", color: "#666" }}>הוצאות עד כה</div>
                                        <div style={{ fontSize: "24px", fontWeight: "bold", color: "#dc3545" }}>
                                            ₪{totalSpent.toLocaleString()}
                                        </div>
                                    </div>
                                    <div style={{ textAlign: "center" }}>
                                        <div style={{ fontSize: "14px", color: "#666" }}>יתרה</div>
                                        <div 
                                            style={{ 
                                                fontSize: "24px", 
                                                fontWeight: "bold",
                                                color: totalRemaining >= 0 ? "#28a745" : "#dc3545"
                                            }}
                                        >
                                            ₪{totalRemaining.toLocaleString()}
                                        </div>
                                    </div>
                                </div>
                                <ProgressBar 
                                    now={Math.min(totalPercentage, 100)} 
                                    variant={getProgressVariant(totalPercentage)}
                                    style={{ height: "25px" }}
                                    label={`${totalPercentage.toFixed(1)}%`}
                                />
                            </div>

                            {/* Categories Table */}
                            <div style={cardStyle}>
                                <h4 style={{ textAlign: "center", marginBottom: "20px" }}>פירוט לפי קטגוריה</h4>
                                <Table dir="RTL" striped bordered hover>
                                    <thead>
                                        <tr>
                                            <th style={{ width: "200px", textAlign: "center" }}>קטגוריה</th>
                                            <th style={{ width: "120px", textAlign: "center" }}>תקציב</th>
                                            <th style={{ width: "120px", textAlign: "center" }}>הוצאות</th>
                                            <th style={{ width: "120px", textAlign: "center" }}>יתרה</th>
                                            <th style={{ textAlign: "center" }}>מימוש</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {budgetData.map((item, index) => {
                                            const percentage = item.budgetLimit > 0 
                                                ? (item.spent / item.budgetLimit) * 100 
                                                : 0;
                                            
                                            return (
                                                <tr key={index}>
                                                    <td style={{ textAlign: "center", fontWeight: "bold" }}>
                                                        {item.categoryName}
                                                    </td>
                                                    <td style={{ textAlign: "center" }}>
                                                        ₪{item.budgetLimit.toLocaleString()}
                                                    </td>
                                                    <td style={{ textAlign: "center", color: "#dc3545" }}>
                                                        ₪{item.spent.toLocaleString()}
                                                    </td>
                                                    <td 
                                                        style={{ 
                                                            textAlign: "center",
                                                            fontWeight: "bold",
                                                            color: item.remaining >= 0 ? "#28a745" : "#dc3545"
                                                        }}
                                                    >
                                                        {getStatusText(item.spent, item.budgetLimit, item.remaining)}
                                                    </td>
                                                    <td style={{ verticalAlign: "middle" }}>
                                                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                                            <ProgressBar 
                                                                now={Math.min(percentage, 100)} 
                                                                variant={getProgressVariant(percentage)}
                                                                style={{ flex: 1, height: "20px" }}
                                                            />
                                                            <span style={{ minWidth: "50px", textAlign: "left" }}>
                                                                {percentage.toFixed(1)}%
                                                            </span>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            </div>
                        </>
                    )}
                </>
            )}
            <Notifier ref={notifierRef} />
        </div>
    );
}
